import cx from 'classnames';

import BreakpointsContainer from '~/components/BreakpointsContainer';
import PostDetails from '~/components/PostCardv3/PostDetails';
import PostImage from '~/components/PostCardv3/PostImage';
import PostLinkHelper from '~/components/PostCardv3/PostLinkHelper';
import PostPremiumBadge from '~/components/PostCardv3/PostPremiumBadge';
import PostTitles from '~/components/PostCardv3/PostTitles';
import Signup from '~/components/Signup';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { Post } from '~/interfaces/post';
import type { HomePageLoaderData } from '~/routes';

const getHeaderPosts = (posts: Post[], featuredPosts: Post[]) => {
  let headliner: any;
  const leftPanelPosts: Post[] = [];
  const rightPanelPosts: Post[] = [];

  // Fill with featured posts first
  featuredPosts.forEach((post, index) => {
    if (index === 0) {
      headliner = post;
    } else if (leftPanelPosts.length < 2) {
      leftPanelPosts.push(post);
    } else {
      rightPanelPosts.push(post);
    }
  });

  // For aesthetics fill out the news section with latest posts
  let rightPanelPostsCount = rightPanelPosts.length;
  let leftPanelPostsCount = leftPanelPosts.length;
  posts.forEach((post) => {
    if (!headliner) {
      headliner = post;
      return;
    }

    if (leftPanelPostsCount < 2) {
      leftPanelPosts.push(post);
      leftPanelPostsCount++;
      return;
    }

    if (rightPanelPostsCount >= 3) {
      return;
    }

    rightPanelPosts.push(post);
    rightPanelPostsCount++;
  });

  return {
    headliner: headliner,
    leftPanelPosts,
    rightPanelPosts,
  };
};

const News = () => {
  const { homePage, paginatedPosts, featuredPosts, signupWidgetConfigs, cardStyles } =
    useTypedLoaderData<HomePageLoaderData>();

  const posts = paginatedPosts.posts;
  const { headliner, leftPanelPosts, rightPanelPosts } = getHeaderPosts(posts, featuredPosts);

  return (
    <BreakpointsContainer className="w-full py-4">
      <div className={cx('mx-auto grid grid-cols-12 gap-0 transition-all md:gap-6')}>
        {/* Left Panel */}
        <div className="col-span-12 w-full md:col-span-3 md:block">
          <div className="space-y-2">
            {leftPanelPosts.map((post, _index) => {
              const isPremium = post.audience === 'premium';

              return (
                <PostLinkHelper
                  key={post.id}
                  slug={post?.slug}
                  className={cx('group flex w-full flex-col transition-all')}
                >
                  <PostImage
                    alt={post.web_title}
                    radiusClass={'rounded-lg'}
                    hasRadiusApplied={true}
                    src={post.image_url}
                  />

                  <div className={cx('flex flex-col justify-between space-y-6 py-4')}>
                    <div className="space-y-1">
                      {isPremium && (
                        <PostPremiumBadge
                          fontFamily={cardStyles.font}
                          bgColor={cardStyles.premium_bg_color}
                          color={cardStyles.premium_text_color}
                        />
                      )}
                      <PostTitles
                        title={{
                          text: post.web_title,
                          fontFamily: homePage?.general_section?.theme?.font,
                          color: cardStyles.heading_font_color,
                          size: 'xl',
                        }}
                        subtitle={{
                          text: post?.web_subtitle || '',
                          fontFamily: homePage?.general_section?.theme?.font,
                          color: cardStyles.description_font_color,
                          size: 'sm',
                        }}
                      />
                      {cardStyles.timefields_enabled && (
                        <PostDetails
                          fontFamily={homePage?.general_section?.theme?.font}
                          color={homePage?.general_section?.theme?.text_color}
                          timestamp={post.override_scheduled_at}
                        />
                      )}
                    </div>
                  </div>
                </PostLinkHelper>
              );
            })}
          </div>
        </div>

        {/* Center Panel */}
        <div className="col-span-12 w-full space-y-4 md:col-span-9 lg:col-span-6">
          {headliner ? (
            <PostLinkHelper slug={headliner?.slug} className={cx('group flex w-full flex-col transition-all')}>
              <PostImage
                alt={headliner.web_title}
                radiusClass={'rounded-lg'}
                hasRadiusApplied={true}
                src={headliner.image_url}
              />
              <div className={cx('flex flex-col justify-between space-y-6 py-4')}>
                <div className="space-y-3">
                  {headliner?.audience === 'premium' && (
                    <PostPremiumBadge
                      fontFamily={cardStyles.font}
                      bgColor={cardStyles.premium_bg_color}
                      color={cardStyles.premium_text_color}
                    />
                  )}
                  <PostTitles
                    title={{
                      text: headliner.web_title,
                      fontFamily: homePage?.general_section?.theme?.font,
                      color: cardStyles.heading_font_color,
                      size: '3xl',
                    }}
                    subtitle={{
                      text: headliner?.web_subtitle || '',
                      fontFamily: homePage?.general_section?.theme?.font,
                      color: cardStyles.description_font_color,
                      size: 'lg',
                    }}
                  />
                  <PostDetails
                    fontFamily={homePage?.general_section?.theme?.font}
                    color={homePage?.general_section?.theme?.text_color}
                    timestamp={headliner?.override_scheduled_at}
                  />
                </div>
              </div>
            </PostLinkHelper>
          ) : null}
          <div className="pb-4">
            <Signup signupStyles={signupWidgetConfigs} />
          </div>
        </div>

        {/* Right Panel */}
        <div className="col-span-12 w-full lg:col-span-3 lg:block">
          <div className="flex flex-col space-y-6">
            {rightPanelPosts.map((post, _index) => {
              const isPremium = post.audience === 'premium';

              return (
                <PostLinkHelper
                  key={post.id}
                  slug={post?.slug}
                  className={cx('group relative flex w-full flex-col space-y-2 transition-all')}
                >
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      {isPremium && (
                        <PostPremiumBadge
                          fontFamily={cardStyles.font}
                          bgColor={cardStyles.premium_bg_color}
                          color={cardStyles.premium_text_color}
                        />
                      )}
                      <PostTitles
                        title={{
                          text: post.web_title,
                          fontFamily: homePage?.general_section?.theme?.font,
                          color: cardStyles.heading_font_color,
                          size: '2xl',
                        }}
                        subtitle={{
                          text: post?.web_subtitle || '',
                          fontFamily: homePage?.general_section?.theme?.font,
                          color: cardStyles.description_font_color,
                          size: 'md',
                        }}
                      />
                      <PostDetails
                        fontFamily={homePage?.general_section?.theme?.font}
                        color={homePage?.general_section?.theme?.text_color}
                        timestamp={post.override_scheduled_at}
                      />
                    </div>
                  </div>
                </PostLinkHelper>
              );
            })}
          </div>
        </div>
      </div>
    </BreakpointsContainer>
  );
};

export default News;
